// Vendor ordering/delivery schedule math — shared by the classic dashboard
// (TaskAlertDashboard) and the AI Command Center's live feed so both surfaces
// compute "upcoming vendor orders" identically. Extracted verbatim from
// TaskAlertDashboard.jsx; pure and unit-testable.

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const WEEKDAY_INDEX_LOOKUP = {
  mon: 0, monday: 0,
  tue: 1, tues: 1, tuesday: 1,
  wed: 2, weds: 2, wednesday: 2,
  thu: 3, thur: 3, thurs: 3, thursday: 3,
  fri: 4, friday: 4,
  sat: 5, saturday: 5,
  sun: 6, sunday: 6
};

export const startOfDay = (value = new Date()) => {
  const dt = value instanceof Date ? new Date(value) : new Date(value);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

export const getWeekdayIndex = (token) => {
  if (!token || typeof token !== 'string') return null;
  const normalized = token.trim().toLowerCase();
  if (!normalized) return null;
  return Object.prototype.hasOwnProperty.call(WEEKDAY_INDEX_LOOKUP, normalized)
    ? WEEKDAY_INDEX_LOOKUP[normalized]
    : null;
};

const expandDayRange = (startIndex, endIndex) => {
  const indexes = [startIndex];
  let current = startIndex;
  while (current !== endIndex) {
    current = (current + 1) % 7;
    indexes.push(current);
    if (indexes.length > 7) break;
  }
  return indexes;
};

export const parseWeekdayTokens = (rawValue) => {
  if (!rawValue) return [];
  const normalized = String(rawValue)
    .toLowerCase()
    .replace(/\band\b/g, ',')
    .replace(/[;]/g, ',')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return [];

  const parts = normalized
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  const indexes = [];
  parts.forEach((part) => {
    const rangeMatch = part.match(/^([a-z]+)\s*-\s*([a-z]+)$/);
    if (rangeMatch) {
      const startIndex = getWeekdayIndex(rangeMatch[1]);
      const endIndex = getWeekdayIndex(rangeMatch[2]);
      if (startIndex !== null && endIndex !== null) {
        expandDayRange(startIndex, endIndex).forEach((idx) => indexes.push(idx));
      }
      return;
    }

    const dayIndex = getWeekdayIndex(part);
    if (dayIndex !== null) indexes.push(dayIndex);
  });

  return [...new Set(indexes)];
};

export const parseScheduleArray = (rawSchedule) => {
  if (!rawSchedule) return [];
  if (Array.isArray(rawSchedule)) return rawSchedule;
  if (typeof rawSchedule === 'string') {
    try {
      const parsed = JSON.parse(rawSchedule);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

export const getJsDayToMondayIndex = (jsDay) => (jsDay + 6) % 7;

export const getDaysAhead = (dateValue, todayStart) => {
  const diffMs = startOfDay(dateValue) - todayStart;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Project a vendor list into dated order/delivery events for the next
 * `daysAhead` days, combining the weekly recurring bitmask (ordering_days) or
 * weekday tokens (delivery_days) with one-off dated schedule entries.
 * Events: { key, vendor, vendorId, date, daysUntil, weekdayLabel, cutoffTime, notes }.
 */
export const buildUpcomingVendorEvents = (vendors, type, daysAhead = 7) => {
  const today = startOfDay();
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + daysAhead - 1);
  const events = [];

  (vendors || []).forEach((vendor) => {
    const recurringDays = new Set();
    const recurringMetadata = new Map();
    const datedEvents = [];

    if (type === 'order') {
      const orderingDays = Number(vendor.ordering_days || 0);
      for (let i = 0; i < 7; i += 1) {
        if (orderingDays & (1 << i)) {
          recurringDays.add(i);
        }
      }
    } else {
      parseWeekdayTokens(vendor.delivery_days).forEach((idx) => recurringDays.add(idx));
    }

    const scheduleEntries = parseScheduleArray(type === 'order' ? vendor.ordering_schedule : vendor.delivery_schedule);
    scheduleEntries.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      if (entry.date) {
        const specificDate = startOfDay(entry.date);
        if (!Number.isNaN(specificDate.getTime())) {
          datedEvents.push({
            date: specificDate,
            cutoffTime: entry.cutoff_time || entry.time || vendor.cutoff_time || null,
            notes: entry.notes || entry.period || null
          });
        }
        return;
      }
      const dayIndex = getWeekdayIndex(entry.day || entry.weekday);
      if (dayIndex === null) return;
      recurringDays.add(dayIndex);
      recurringMetadata.set(dayIndex, {
        cutoffTime: entry.cutoff_time || entry.time || vendor.cutoff_time || null,
        notes: entry.notes || entry.period || null
      });
    });

    for (let offset = 0; offset < daysAhead; offset += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + offset);
      const mondayIndex = getJsDayToMondayIndex(date.getDay());
      if (!recurringDays.has(mondayIndex)) continue;
      const metadata = recurringMetadata.get(mondayIndex) || {};
      events.push({
        key: `${type}-${vendor.id || vendor.display_name}-${date.toISOString().slice(0, 10)}`,
        vendor: vendor.display_name || 'Unknown Vendor',
        vendorId: vendor.id || vendor.display_name,
        date,
        daysUntil: offset,
        weekdayLabel: WEEKDAY_SHORT[mondayIndex],
        cutoffTime: metadata.cutoffTime || (type === 'order' ? vendor.cutoff_time || null : null),
        notes: metadata.notes || null
      });
    }

    datedEvents.forEach((entry, index) => {
      if (entry.date < today || entry.date > endDate) return;
      const mondayIndex = getJsDayToMondayIndex(entry.date.getDay());
      events.push({
        key: `${type}-dated-${vendor.id || vendor.display_name}-${index}-${entry.date.toISOString().slice(0, 10)}`,
        vendor: vendor.display_name || 'Unknown Vendor',
        vendorId: vendor.id || vendor.display_name,
        date: entry.date,
        daysUntil: getDaysAhead(entry.date, today),
        weekdayLabel: WEEKDAY_SHORT[mondayIndex],
        cutoffTime: entry.cutoffTime || (type === 'order' ? vendor.cutoff_time || null : null),
        notes: entry.notes || null
      });
    });
  });

  const dedupedMap = new Map();
  events.forEach((event) => {
    const dedupeKey = `${event.type || type}-${event.vendorId}-${event.date.toISOString().slice(0, 10)}`;
    if (!dedupedMap.has(dedupeKey)) dedupedMap.set(dedupeKey, event);
  });

  return [...dedupedMap.values()].sort((a, b) => a.date - b.date);
};
